import storefront from "../global/storefront";
import { PRODUCT_SWATCH_COLOR_PATTERNS_QUERY } from "../global/storefront/queries";

const MAX_IDS_PER_NODES_REQUEST = 250;

const swatchDataCache = new Map();

function chunk(array, size) {
  const chunks = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
}

function toProductGid(id) {
  return id.startsWith("gid://") ? id : `gid://shopify/Product/${id}`;
}

function extractSwatchData(node) {
  const colorPatternNode =
    node && node.colorPattern && node.colorPattern.references && node.colorPattern.references.nodes[0];

  if (!colorPatternNode) {
    return null;
  }

  return {
    imageUrl:
      (colorPatternNode.swatchImage &&
        colorPatternNode.swatchImage.reference &&
        colorPatternNode.swatchImage.reference.image &&
        colorPatternNode.swatchImage.reference.image.url) ||
      null,

    color: (colorPatternNode.swatchColor && colorPatternNode.swatchColor.value) || null,

    label: (colorPatternNode.swatchLabel && colorPatternNode.swatchLabel.value) || null,
  };
}

async function fetchSwatchData(productIds) {
  const batches = chunk(productIds, MAX_IDS_PER_NODES_REQUEST);

  const responses = await Promise.all(
    batches.map((batchIds) =>
      storefront
        .request({
          query: PRODUCT_SWATCH_COLOR_PATTERNS_QUERY,
          variables: {
            ids: batchIds.map(toProductGid),
          },
        })
        .catch((error) => {
          console.error("[product-swatch-hydration] Storefront batch request failed:", error);

          return undefined;
        }),
    ),
  );

  batches.forEach((batchIds, batchIndex) => {
    const response = responses[batchIndex];
    const nodes = response && response.data && response.data.nodes;

    if (!Array.isArray(nodes)) {
      return;
    }

    nodes.forEach((node, nodeIndex) => {
      const productId = batchIds[nodeIndex];

      swatchDataCache.set(productId, extractSwatchData(node));
    });
  });
}

function applySwatchData(link, data) {
  const colorEl = link.querySelector(".product-swatch__color");
  const labelEl = link.querySelector(".product-swatch__label, .product-swatch__tooltip");

  if (data) {
    if (colorEl) {
      colorEl.classList.remove("product-swatch__color--placeholder");

      if (data.imageUrl) {
        colorEl.style.backgroundImage = `url(${data.imageUrl})`;
        colorEl.style.backgroundSize = "cover";
      } else if (data.color) {
        colorEl.style.backgroundColor = data.color;
      }
    }

    if (data.label) {
      const label = data.label.split(" - ")[0].trim();

      if (labelEl) {
        labelEl.textContent = label;
      }

      link.setAttribute("title", label);
      link.setAttribute("aria-label", label);

      // Keep display identity synchronized.
      link.dataset.colorLabel = label;
    }
  }

  link.removeAttribute("data-product-swatch-pending");
  link.setAttribute("data-product-swatch-hydrated", "");
}

export async function hydrateProductSwatches(root = document) {
  const pendingLinks = Array.from(root.querySelectorAll("[data-product-swatch-pending]"));

  if (!pendingLinks.length) {
    return;
  }

  const productIds = [...new Set(pendingLinks.map((link) => link.dataset.productId))];
  const uncachedIds = productIds.filter((id) => !swatchDataCache.has(id));

  if (uncachedIds.length) {
    await fetchSwatchData(uncachedIds);
  }

  pendingLinks.forEach((link) => {
    applySwatchData(link, swatchDataCache.get(link.dataset.productId));
  });
}

hydrateProductSwatches(document);

document.addEventListener("product:ajax-rendered", (event) => {
  hydrateProductSwatches(event.detail.section || document);
});
